import { DataTableActionBarAction } from "@/components/data-table/data-table-action-bar";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogTitle, DialogContent, DialogHeader, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@/components/ui/date-picker";
import { useAccountTimezone } from "@/hooks/use-account-timezone";
import { billingDateChange } from "@/lib/billing-date-change";
import { useBulkUpdateBillingCycle } from "@/lib/api/subscriptions";

interface UpdateBillingCycleBulkActionModalProps {
    subscriptionIds: string[];
    size?: 'sm' | 'md';
}

export const UpdateBillingCycleBulkActionModal = ({ subscriptionIds, size = 'sm' }: UpdateBillingCycleBulkActionModalProps) => {
    const { t } = useTranslation('subscriptions');
    const { mutate: updateBillingCycleMutation, isPending: isUpdatingBillingCycle } = useBulkUpdateBillingCycle();
    const { timezone, isReady: isTimezoneReady } = useAccountTimezone();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date(new Date().setHours(0, 0, 0, 0)));
    const [selectedTime, setSelectedTime] = useState('');

    const isSubmitDisabled = isUpdatingBillingCycle || !selectedDate || (selectedTime !== '' && !isTimezoneReady);

    const handleDateChange = (date: Date | undefined) => {
        setSelectedDate(date ?? null);
    };

    const handleOpen = () => {
        setSelectedTime('');
        setIsOpen(true);
    };

    const handleSubmit = () => {
        if (!selectedDate) return;
        const change = billingDateChange(selectedDate, selectedTime, timezone);
        updateBillingCycleMutation({ subscriptionIds, ...change }, {
            onSettled: () => {
                setIsOpen(false);
            },
        });
        setIsOpen(false);

    };

    const handleOpenChange = (open: boolean) => {
        if (!open) setIsOpen(false);
    };

    return (
        <>
            {size === 'sm' && (
                <DataTableActionBarAction
                    tooltip={t('bulkActions.updateBillingCycle.tooltip')}
                    isPending={isUpdatingBillingCycle}
                    onClick={handleOpen}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {t('bulkActions.updateBillingCycle.button')}
                </DataTableActionBarAction>
            )}
            {size === 'md' && (
                <Button variant="outline" size="sm" onClick={handleOpen}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {t('bulkActions.updateBillingCycle.button')}
                </Button>
            )}
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('bulkActions.updateBillingCycle.title')}</DialogTitle>
                        <DialogDescription>
                            {t('bulkActions.updateBillingCycle.confirmMessage', { count: subscriptionIds.length })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 relative">
                        <DatePicker value={selectedDate} onChange={(d) => handleDateChange(d ?? new Date())} />
                        <div className="space-y-2">
                            <Label htmlFor="billing-cycle-time">{t('bulkActions.updateBillingCycle.selectTime')}</Label>
                            <Input id="billing-cycle-time" type="time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} />
                            <p className="text-xs text-muted-foreground">{t('bulkActions.updateBillingCycle.timeHint', { timezone })}</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIsOpen(false)} variant="outline">
                            {t('bulkActions.updateBillingCycle.cancel')}
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
                            {isUpdatingBillingCycle && <Loader2 className="size-4 animate-spin mr-2" />}
                            {t('bulkActions.updateBillingCycle.update')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}